import { assertNever } from '@dadohr/shared/domainTypes/00-assertNever';
import {
  ISubTaskDataFieldChangeDenormalized,
  ISubtaskDataFieldChangeTemplate,
  ISubTaskMeetTemplate,
  SubTaskDenormalized,
  SubTaskTemplate,
  SubTaskType
} from '@dadohr/shared/domainTypes/experience';
import { Link as MuiLink, Typography } from '@material-ui/core';
import { capitalize } from 'lodash';
import { RuleRenderer } from '../../features/Experiences/components/RuleBuilder';
import { useDoceboConfig } from '../../features/Org/pages/Integrations/integrations/docebo/service';
import {
  TFunction,
  UnsafeTFunction,
  useTranslation,
  useUnsafeTranslation
} from '../../i18next';
import { useCurrentUser } from '../../services/currentUser';
import { useUserRuleConfigSimple } from '../../services/user';

export const meetWithPersonAsText = (
  role: ISubTaskMeetTemplate['data']['role'],
  t: TFunction
) => {
  switch (role) {
    case 'MANAGER':
      return t('subTask.meet.options.manager');
    case 'MENTOR':
      return t('subTask.meet.options.mentor');
    case 'SKIP_LEVEL_MANAGER':
      return t('subTask.meet.options.skipLevelManager');
    case 'HR':
      return t('subTask.meet.options.hr');
    case 'TEAM':
      return t('subTask.meet.options.team');
    default:
      return assertNever(role);
  }
};

const capitalize0 = (str: string) => {
  if (!str.length) {
    return str;
  }
  return `${str[0].toUpperCase()}${str.slice(1)}`;
};

const DataFieldChangeSubTaskText = ({
  s
}: {
  s: ISubtaskDataFieldChangeTemplate | ISubTaskDataFieldChangeDenormalized;
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const config = useUserRuleConfigSimple(
    currentUser?.org.customUserFields || [],
    currentUser?.org.tz || 'UTC',
    {
      experienceTemplateId: null,
      orgId: currentUser?.org.id || null
    }
  );

  if (!currentUser) {
    return null;
  }

  return (
    <span>
      {capitalize(t('subTask.dataFieldChange.verb'))}{' '}
      {t('subTask.dataFieldChange.label')}:{' '}
      <RuleRenderer value={s.data.rules} variant={'inline'} config={config} />
    </span>
  );
};

// Renders a link to the org's Docebo tenant homepage. Isolated into its own
// component so the `useDoceboConfig` listener only mounts when we have a current
// user (valid orgId/userId). The admin-side denormalizer doesn't populate
// `s.data.subdomain`, so we read it live from the integration config here.
const DoceboHomepageLink = ({
  orgId,
  userId
}: {
  orgId: string;
  userId: string;
}) => {
  const { t } = useUnsafeTranslation();
  const [doceboConfig] = useDoceboConfig(orgId, userId);
  const subdomain = doceboConfig?.data.subdomain;
  if (!subdomain) {
    return null;
  }
  return (
    <MuiLink
      href={`https://${subdomain}.docebosaas.com/learn/mycourses`}
      target="_blank"
      rel="noopener noreferrer"
      color="primary"
      underline="hover"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        whiteSpace: 'nowrap',
        fontWeight: 500,
      }}
    >
      <img
        src="/images/integrations-logos/docebo-tiny-logo.svg"
        alt=""
        width={14}
        height={14}
        style={{ objectFit: 'contain' }}
      />
      {t('Open your Docebo homepage')}
    </MuiLink>
  );
};

// Admin-app render for a "Complete LMS module" subtask: the course label plus an
// inline link to the Docebo tenant homepage. Kept inline (not a button) so it sits
// cleanly on the compact caption rows SubTaskAsText is used in (preview summary,
// timeline detail, tables).
const CompleteLmsModuleSubTaskText = ({
  s
}: {
  s: SubTaskTemplate | SubTaskDenormalized;
}) => {
  const { t } = useUnsafeTranslation();
  const currentUser = useCurrentUser();
  const unitName = (s.data as { unit?: { name?: string } }).unit?.name;
  const supportEmail = (s.data as { supportContact?: { email?: string } })
    .supportContact?.email;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        columnGap: 8,
        rowGap: 2
      }}
    >
      <span>{`${capitalize(t('Complete'))} ${unitName || t('LMS module')}`}</span>
      {currentUser && (
        <DoceboHomepageLink orgId={currentUser.org.id} userId={currentUser.id} />
      )}
      {supportEmail && (
        <Typography variant="caption" color="textSecondary" component="span">
          {t('Something not working? Contact')}{' '}
          <MuiLink href={`mailto:${supportEmail}`} color="inherit">
            {supportEmail}
          </MuiLink>
        </Typography>
      )}
    </span>
  );
};

export const subTaskToText = (
  s: SubTaskTemplate | SubTaskDenormalized,
  t: UnsafeTFunction
) => {
  switch (s.type) {
    case SubTaskType.CONFIRM:
      return `${capitalize(t('subTask.confirm.verb'))} ${s.data.label}`;
    case SubTaskType.MEET:
      return `${capitalize(t('subTask.meet.verb'))} ${meetWithPersonAsText(
        s.data.role,
        t
      )}`;
    case SubTaskType.CREATE_MEETING:
      return `${capitalize(t('Book a meeting'))}: ${s.data.title}`;
    case SubTaskType.SUBMIT:
      return `${capitalize(t('subTask.submit.verb'))} ${
        s.data.label || t('file')
      }`;
    case SubTaskType.WATCH:
      return `${capitalize(t('subTask.watch.verb'))} ${
        s.data.label || t('video')
      }`;
    case SubTaskType.VISIT:
      return `${capitalize(t('subTask.visit.verb'))} ${s.data.label}`;
    case SubTaskType.QUESTION:
      if (s.data.question.type === 'address') {
        return `${capitalize(t('subTask.question.provideAddress.verb'))} ${
          s.data.question.label ? `"${s.data.question.label}"` : ''
        }`;
      }
      return `${capitalize(t('subTask.question.verb'))} "${
        s.data.question.label
      }"`;
    case SubTaskType.CALENDLY_INVITE:
      return capitalize0(
        t('subTask.calendlyInvite.verb', {
          eventName: s.data.event?.name || ''
        })
      );
    case SubTaskType.DOCUMENT_READ:
      return `${capitalize(t('subTask.documentRead.verb'))} "${s.data.title}"`;
    case SubTaskType.SWAG_UP_CHOICE:
      return `${capitalize(t('subTask.swagUpChoice.verb'))}`;
    case SubTaskType.DATA_FIELD_CHANGE:
      return `${capitalize(t('subTask.dataFieldChange.verb'))} ${t(
        'subTask.dataFieldChange.label'
      )}`;
    case SubTaskType.DOCUSIGN_ENVELOPE_SIGN:
      return t('Sign Document'); // TODO Docusign
    case SubTaskType.BACKGROUND_CHECK_INVITE:
      return t('subTask.backgroundCheck.verb');
    case SubTaskType.COMPLETE_LMS_MODULE:
      return `${capitalize(t('Complete'))} ${s.data.unit.name || t('LMS module')}`;
    default:
      return assertNever(s);
  }
};

// @guide Create a new subtask - 5.1 Create a textual represenation (app)
export const SubTaskAsText = ({
  s
}: {
  s: SubTaskTemplate | SubTaskDenormalized;
}) => {
  const { t } = useUnsafeTranslation();

  switch (s.type) {
    case SubTaskType.DATA_FIELD_CHANGE:
      //TODO: Add something to do here - previously a bunch of unused variables were set and did nothing - look in ch7695 for more details
      return <DataFieldChangeSubTaskText s={s} />;
    default:
      return <span>{subTaskToText(s, t)}</span>;
  }
};
